import { useDispatch, useSelector } from 'react-redux';
import {
  setDiscountCode,
  setBasketPrices,
  addToCart,
  removeItem,
  updateItem,
  setBasketStore,
} from '../../store/basket/basketSlice';
import { apiCalculateBasket } from '../../services/OrderService';
import toast from '../../components/ui/toast/toast';
import Notification from '../../components/ui/Notification';
import { createGuid } from '../createGuid';
import { getNumberFormat } from '../getNumberFormat';
import {
  isBXGYCoupon,
  isSpendGetFreeItemCoupon,
  isSpendGetOffCoupon,
  isItemWithCouponBenefits,
  couponRefusedWithReason,
  dropCouponWithReason,
} from '../coupon';
import { buildCalculateBasketPayload } from '../basketCalculatePayload';

function useCouponHandler(menu) {
  const dispatch = useDispatch();
  const basket = useSelector((state) => state.basket.basket);
  const currentStore = useSelector((state) => state.base.common.currentStore);

  // Helper function to find item in menu by itemId
  const findItemInMenu = (itemId) => {
    // Try to find in menu prop first
    if (menu?.categories) {
      for (const category of menu.categories) {
        for (const product of category.products || []) {
          const foundItem = product.items?.find((item) => item.id === itemId);
          if (foundItem) {
            return { item: foundItem, product };
          }
        }
      }
    }

    // If not found, try to find in currentStore.menus (array of menus)
    if (currentStore?.menus && Array.isArray(currentStore.menus)) {
      for (const storeMenu of currentStore.menus) {
        if (storeMenu?.categories) {
          for (const category of storeMenu.categories) {
            for (const product of category.products || []) {
              const foundItem = product.items?.find(
                (item) => item.id === itemId,
              );
              if (foundItem) {
                return { item: foundItem, product };
              }
            }
          }
        }
      }
    }

    return null;
  };

  // Helper function to find product in menu by productId
  const findProductInMenu = (productId) => {
    // Try to find in menu prop first
    if (menu?.categories) {
      for (const category of menu.categories) {
        const foundProduct = category.products?.find(
          (product) => product.id === productId,
        );
        if (foundProduct) {
          return foundProduct;
        }
      }
    }

    // If not found, try to find in currentStore.menus (array of menus)
    if (currentStore?.menus && Array.isArray(currentStore.menus)) {
      for (const storeMenu of currentStore.menus) {
        if (storeMenu?.categories) {
          for (const category of storeMenu.categories) {
            const foundProduct = category.products?.find(
              (product) => product.id === productId,
            );
            if (foundProduct) {
              return foundProduct;
            }
          }
        }
      }
    }

    return null;
  };

  // Ensure basketStore is set when adding coupon items to an empty basket
  // Prevents false "different restaurant" warning when adding more items later
  const ensureBasketStore = () => {
    if (!basket.basketStore && currentStore) {
      dispatch(
        setBasketStore({
          storeSlug: currentStore.slug,
          name: currentStore.name,
          storelogo: currentStore.storelogo,
          address: currentStore.address,
          apartment: currentStore.apartment,
          city: currentStore.city,
          state: currentStore.state,
          postalcode: currentStore.postalcode,
        })
      );
    }
  };

  const handleApplyCoupon = async (coupon, bxgySelection = null) => {
    try {
      // All coupon types can be applied with empty basket
      // Type 0: User can apply coupon and add free item, minimum price will be checked at payment
      // Type 1: User can apply coupon, minimum price will be checked at payment
      // Type 2: User will add item via modal

      // Only one coupon can be used per basket
      // If the same coupon is already applied, do nothing
      if (basket.discountcode === coupon.code) {
        toast.push(
          <Notification
            title="Coupon Already Applied"
            type="info"
            duration={2000}
          >
            This coupon is already applied to your order
          </Notification>,
          {
            placement: 'top-center',
          },
        );
        return;
      }

      // Check minimum price requirement BEFORE removing previous coupon
      // Skip for all types - minimum price will be checked at payment
      // Type 0: User can add free item, minimum price checked at payment
      // Type 1: Minimum price checked at payment
      // Type 2: User will add item via modal

      let cleanPrices = null;

      // Now that we know the new coupon requirements are met,
      // clear the previous coupon code (but keep items in basket)
      // Free items and BXGY items will become normal paid items
      if (basket.discountcode && basket.discountcode !== coupon.code) {
        // Clear previous discount code
        dispatch(setDiscountCode(''));

        // Recalculate basket with all items (free items become paid, BXGY items become normal)
        // This will update prices for items that were previously free or discounted
        if (basket.items.length > 0) {
          try {
            const basketDataWithoutCoupon = {
              ...buildCalculateBasketPayload(basket, currentStore.id),
              discountcode: '',
            };

            // Recalculate to get clean basket prices (free items become paid)
            const cleanResponse = await apiCalculateBasket(
              basketDataWithoutCoupon,
            );
            cleanPrices =
              cleanResponse?.data?.data || cleanResponse?.data || cleanResponse;
            if (cleanPrices) {
              dispatch(setBasketPrices(cleanPrices));
            }

            // Update free items to have normal prices (not 0)
            // Find free items (items with price === 0 and total === 0)
            const freeItems = basket.items.filter(
              (item) => item.item?.price === 0 && item.total === 0,
            );

            // Update each free item with normal price from menu
            for (const freeItem of freeItems) {
              const itemData = findItemInMenu(freeItem.item.id);
              if (itemData) {
                const itemPrice = itemData.item.price || 0;
                const optionsTotal = (freeItem.options || []).reduce(
                  (sum, opt) => sum + (opt.price || 0) * (opt.qty ?? 1),
                  0
                );
                const normalTotal = (itemPrice + optionsTotal) * freeItem.qty;

                // Update item with normal price
                dispatch(
                  updateItem({
                    basketId: freeItem.basketId,
                    item: {
                      ...freeItem.item,
                      price: itemPrice,
                    },
                    total: normalTotal,
                    // It is an ordinary paid line now: it must not read as
                    // the previous coupon's free line any more.
                    isFreeItemFromCoupon: false,
                  })
                );
              }
            }

            // Update BXGY items to remove bxgyInfo and calculate normal prices
            const bxgyItems = basket.items.filter((item) => item.bxgyInfo);
            for (const bxgyItem of bxgyItems) {
              const itemData = findItemInMenu(bxgyItem.item.id);
              if (itemData) {
                const itemPrice = itemData.item.price || bxgyItem.item.price || 0;
                const optionsTotal = (bxgyItem.options || []).reduce(
                  (sum, opt) => sum + (opt.price || 0) * (opt.qty ?? 1),
                  0
                );
                const normalTotal = (itemPrice + optionsTotal) * bxgyItem.qty;

                // Update item to remove bxgyInfo and set normal price
                dispatch(
                  updateItem({
                    basketId: bxgyItem.basketId,
                    item: {
                      ...bxgyItem.item,
                      price: itemPrice,
                    },
                    total: normalTotal,
                    bxgyInfo: undefined, // Remove bxgyInfo
                  })
                );
              }
            }
          } catch (cleanError) {
            console.error('Error recalculating basket without coupon:', cleanError);
            // Continue with new coupon application even if cleanup fails
          }
        }
      }

      // Handle different coupon types
      if (isSpendGetFreeItemCoupon(coupon)) {
        // Tip 0: Spend $XX Get Free Item
        // Step 1: Validate coupon configuration
        if (!coupon.freeitemid) {
          toast.push(
            <Notification title="Error" type="danger" duration={3000}>
              Invalid coupon configuration
            </Notification>,
            {
              placement: 'top-center',
            },
          );
          return;
        }

        // Step 2: Find the free item in menu
        const itemData = findItemInMenu(coupon.freeitemid);
        if (!itemData) {
          toast.push(
            <Notification title="Error" type="danger" duration={3000}>
              Free item not found in menu
            </Notification>,
            {
              placement: 'top-center',
            },
          );
          return;
        }

        // Step 3: Check if free item (from coupon) already exists in basket
        // Only check items flagged as isFreeItemFromCoupon — manually added items with the same ID are separate
        const freeItemExists = basket.items.some(
          (item) =>
            item.item.id === coupon.freeitemid &&
            item.isFreeItemFromCoupon === true,
        );
        if (freeItemExists) {
          toast.push(
            <Notification
              title="Free Item Already Added"
              type="info"
              duration={3000}
            >
              Free {itemData.item.name} is already in your basket
            </Notification>,
            {
              placement: 'top-center',
            },
          );
          dispatch(setDiscountCode(coupon.code));
          return;
        }

        // Step 4: If no selection provided, return to trigger modal
        // For free item coupons, user needs to select sub-options via modal
        if (!bxgySelection) {
          return {
            type: 'FREE_ITEM_SELECTION_REQUIRED',
            product: itemData.product,
            coupon,
            lockedItemId: coupon.freeitemid,
          };
        }

        // Step 5: Set discount code first
        dispatch(setDiscountCode(coupon.code));

        // Step 6: bxgySelection contains the selected item with options
        // For free item, it's actually the free item with sub-options selected
        const { item, quantity, options: selectedOptions } = bxgySelection;

        // Step 7: Apply coupon to basket WITH free item (to calculate extra option prices)
        // Free item is included in API call so backend can calculate extra option prices
        const baseBasketData = buildCalculateBasketPayload(
          basket,
          currentStore.id,
        );
        const basketDataWithFreeItem = {
          ...baseBasketData,
          discountcode: coupon.code,
          items: [
            ...baseBasketData.items,
            {
              itemid: item.id,
              qty: quantity || 1,
              // Wire: TOTAL units = unit qty x line qty (legacy options -> 1).
              options: (selectedOptions || []).map((option) => ({
                optionid: option.id,
                qty: (option.qty ?? 1) * (quantity || 1),
              })),
            },
          ],
        };

        const couponResponse = await apiCalculateBasket(basketDataWithFreeItem);
        const couponPrices =
          couponResponse?.data?.data || couponResponse?.data || couponResponse;

        if (
          !couponPrices ||
          (couponPrices.subtotal === undefined &&
            couponPrices.total === undefined)
        ) {
          throw new Error('Invalid response format');
        }

        // Owner, 2026-10-07: the server refused the code for this cart (a
        // coupon limited to menus). Nothing was added yet; drop the code set
        // at step 5 and say why. Never add the free line. No lines are passed:
        // this coupon added none, and `basket.items` may still hold a previous
        // coupon's lines the replace flow already turned into paid ones.
        const reason = couponRefusedWithReason(couponPrices);
        if (reason) {
          dropCouponWithReason(dispatch, [], reason);
          return;
        }

        // Step 8: Add free item to Redux basket (qty=1, price=0.00)
        // Free item's base price is 0, but extra options may have prices
        // Backend calculated the total with extra options included
        const freeItemPayload = {
          basketId: createGuid(),
          product: itemData.product,
          item: {
            name: item.name,
            price: 0, // Free item - base price is 0
            fee: 0, // Free item - no fee
            id: item.id,
          },
          options: selectedOptions || [],
          note: '',
          qty: quantity || 1,
          total: 0, // Free item - base total is 0 (extra options included in basket total)
          isFreeItemFromCoupon: true, // Flag to identify this as the actual free item from coupon
          couponCode: coupon.code, // The coupon that added this line (a refusal drops only its own)
        };

        ensureBasketStore();
        dispatch(addToCart(freeItemPayload));

        // Step 9: Update prices from API response
        // Backend response includes free item with extra options prices
        dispatch(setBasketPrices(couponPrices));

        toast.push(
          <Notification title="Coupon Applied!" type="success" duration={3000}>
            🎁 Free {itemData.product?.name} has been added to your order!
          </Notification>,
          {
            placement: 'top-center',
          },
        );
      } else if (isSpendGetOffCoupon(coupon)) {
        // Type 1: Spend $XX Get $XX Off
        // Set discount code
        dispatch(setDiscountCode(coupon.code));

        // Calculate basket with coupon
        const basketData = {
          ...buildCalculateBasketPayload(basket, currentStore.id),
          discountcode: coupon.code,
        };

        const response = await apiCalculateBasket(basketData);
        // Handle different response formats
        const basketPrices = response?.data?.data || response?.data || response;

        if (
          basketPrices &&
          (basketPrices.subtotal !== undefined ||
            basketPrices.total !== undefined)
        ) {
          // Owner, 2026-10-07: refused by the server (menu rule): drop the
          // code and say why instead of "Coupon Applied!". No lines passed:
          // this coupon added none.
          const reason = couponRefusedWithReason(basketPrices);
          if (reason) {
            dropCouponWithReason(dispatch, [], reason);
            return;
          }
          dispatch(setBasketPrices(basketPrices));
          toast.push(
            <Notification
              title="Coupon Applied!"
              type="success"
              duration={3000}
            >
              💰 ${coupon.discountamount} off has been applied to your order!
            </Notification>,
            {
              placement: 'top-center',
            },
          );
        } else {
          throw new Error('Invalid response format');
        }
      } else if (isBXGYCoupon(coupon)) {
        // Type 2: Buy X Get Y (BXGY)
        const productId =
          coupon.getProductItemId ||
          coupon.getproductitemid ||
          coupon.getProductItemId;
        const product = findProductInMenu(productId);
        if (!product) {
          console.error('Product not found:', {
            productId,
            coupon,
            menu,
            currentStoreMenus: currentStore?.menus,
          });
          toast.push(
            <Notification title="Error" type="danger" duration={3000}>
              Product not found for this coupon. Product ID: {productId}
            </Notification>,
            {
              placement: 'top-center',
            },
          );
          return;
        }

        // If selection is not provided, return to trigger modal
        if (!bxgySelection) {
          return {
            type: 'BXGY_SELECTION_REQUIRED',
            product,
            coupon,
          };
        }

        // bxgySelection contains: { item, quantity, paidQuantity, freeQuantity, options }
        const {
          item,
          quantity,
          paidQuantity,
          freeQuantity,
          options: selectedOptions = [],
        } = bxgySelection;

        // Calculate item total
        // BXGY: Main item paid only for paidQty, options paid for totalQty (all items)
        const itemPrice = item.price || 0;
        const itemFee = item.itemfee || 0;
        const mainItemTotal = itemPrice * paidQuantity + itemFee * paidQuantity;
        const optionsTotal = selectedOptions.reduce(
          (sum, opt) => sum + (opt.price * (opt.qty ?? 1) * quantity || 0), // options for ALL items (totalQuantity)
          0,
        );
        const itemTotal = mainItemTotal + optionsTotal;

        // Add BXGY item to Redux basket
        const bxgyItemPayload = {
          basketId: createGuid(),
          product: product,
          item: {
            name: item.name,
            price: itemPrice,
            fee: itemFee,
            id: item.id,
          },
          options: selectedOptions,
          note: '',
          qty: quantity, // Total quantity (paid + free)
          total: itemTotal, // Only paid quantity price (including options)
          bxgyInfo: {
            paidQuantity,
            freeQuantity,
            totalQuantity: quantity,
          },
          couponCode: coupon.code, // The coupon that added this line (a refusal drops only its own)
        };

        ensureBasketStore();
        dispatch(addToCart(bxgyItemPayload));

        // Set discount code
        dispatch(setDiscountCode(coupon.code));

        // Recalculate basket with BXGY item and coupon
        const baseBasketData = buildCalculateBasketPayload(
          basket,
          currentStore.id,
        );
        const basketData = {
          ...baseBasketData,
          discountcode: coupon.code,
          items: [
            ...baseBasketData.items,
            {
              itemid: item.id,
              qty: quantity, // Total quantity
              // Wire: TOTAL units = unit qty x line qty (options apply to
              // all BXGY copies; legacy options -> 1).
              options: selectedOptions.map((option) => ({
                optionid: option.id,
                qty: (option.qty ?? 1) * quantity,
              })),
            },
          ],
        };

        const response = await apiCalculateBasket(basketData);
        const basketPrices = response?.data?.data || response?.data || response;

        if (
          basketPrices &&
          (basketPrices.subtotal !== undefined ||
            basketPrices.total !== undefined)
        ) {
          // Owner, 2026-10-07: refused by the server (menu rule). The bonus
          // line and the code went in before this calculate; `basket.items`
          // here is the render-time closure and does not hold that line, so
          // remove it by its basketId, then drop the code and say why. No
          // other lines passed: they may be a previous coupon's, now paid.
          const reason = couponRefusedWithReason(basketPrices);
          if (reason) {
            dispatch(removeItem(bxgyItemPayload.basketId));
            dropCouponWithReason(dispatch, [], reason);
            return;
          }
          dispatch(setBasketPrices(basketPrices));
          toast.push(
            <Notification
              title="Coupon Applied!"
              type="success"
              duration={3000}
            >
              🛒 {quantity}x {product.name} = ${getNumberFormat(itemTotal)} (
              {paidQuantity} paid + {freeQuantity} free)
            </Notification>,
            {
              placement: 'top-center',
            },
          );
        } else {
          throw new Error('Invalid response format');
        }
      } else {
        // Unknown coupon type
        toast.push(
          <Notification title="Error" type="danger" duration={3000}>
            Unknown coupon type
          </Notification>,
          {
            placement: 'top-center',
          },
        );
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      toast.push(
        <Notification title="Error" type="danger" duration={3000}>
          Failed to apply coupon. Please try again.
        </Notification>,
        {
          placement: 'top-center',
        },
      );
    }
  };

  return { handleApplyCoupon };
}

export default useCouponHandler;
